'use client';
import React from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { BiSelectMultiple, BiTime } from 'react-icons/bi';
import { RiSparkling2Line } from 'react-icons/ri';
import HowItWorksStep from '@/components/blocks/HowItWorksStep';
import { HiOutlineGlobeAlt } from 'react-icons/hi2';
import { BsCode, BsPalette } from 'react-icons/bs';

type Props = { locale: string };

function SiteScanHowItWorks({ locale }: Props) {
  const { t } = useTranslation(locale);
  const stepsList = t('PlugAndPlay/howItWorks/steps', { returnObjects: true }) as Array<any>;

  const steps = [
    {
      icon: HiOutlineGlobeAlt,
      title: t(stepsList[0].title),
      description: t(stepsList[0].description),
    },
    {
      icon: BiSelectMultiple,
      title: t(stepsList[1].title),
      description: t(stepsList[1].description),
    },
    {
      icon: BiTime,
      title: t(stepsList[2].title),
      description: t(stepsList[2].description),
    },
    {
      icon: BsCode,
      title: t(stepsList[3].title),
      description: t(stepsList[3].description),
    },
  ];

  return (
    <section className="tw-py-10 lg:tw-py-10 tw-bg-transparent tw-font-['Lexend']">
      <div className="tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
        {/* How It Works Section */}
        <div className="tw-mb-32">
          <div className="tw-text-center tw-mb-16">
            <div className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-blue-100 tw-text-blue-700 tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-medium tw-mb-6">
              <RiSparkling2Line className="tw-w-4 tw-h-4" />
              {t('PlugAndPlay/howItWorks/badge')}
            </div>
            <h2 className="tw-text-3xl sm:tw-text-4xl lg:tw-text-5xl tw-font-bold tw-text-gray-900 tw-mb-6 tw-leading-tight">
              {t('PlugAndPlay/howItWorks/title')}
            </h2>
            <p className="tw-text-lg tw-text-gray-600 tw-max-w-3xl tw-mx-auto tw-leading-relaxed">
              {t('PlugAndPlay/howItWorks/description')}
            </p>
          </div>

          <div className="tw-space-y-0 lg:tw-space-y-16">
            {steps.map((step, index) => (
              <div key={index} className="tw-relative">
                <HowItWorksStep
                  step={index + 1}
                  icon={step.icon}
                  title={step.title}
                  description={step.description}
                  isReversed={index % 2 === 1}
                  isLast={index === steps.length - 1}
                />

                {/* Enhanced connection line for desktop */}
                {index < steps.length - 1 && (
                  <div className="tw-hidden lg:tw-block tw-absolute tw-top-full tw-left-1/2 tw-transform tw--translate-x-1/2 tw-z-10">
                    <div className="tw-flex tw-flex-col tw-items-center tw-space-y-2">
                      <div className="tw-w-0.5 tw-h-8 tw-bg-gradient-to-b tw-from-emerald-300 tw-to-cyan-300 tw-rounded-full"></div>
                      <div className="tw-w-3 tw-h-3 tw-bg-gradient-to-br tw-from-emerald-400 tw-to-cyan-500 tw-rounded-full tw-animate-pulse"></div>
                      <div className="tw-w-0.5 tw-h-8 tw-bg-gradient-to-b tw-from-emerald-300 tw-to-cyan-300 tw-rounded-full"></div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default SiteScanHowItWorks;
